import { api, apiBlobRequest } from '@/lib/api-client'
import type {
  InstapayConfig,
  InstapayConfigUpdate,
  PaymentMethodChangeRequest,
  PaymentProofUploadResponse,
  PaymentVerificationDetails,
  PointsTransaction,
} from '@/types/api'

/**
 * Payment verification, proof viewing and the Instapay account.
 *
 * Everything here is keyed on the **trip request id**, not the trip id. A trip
 * has no payment of its own — it inherits the one made on the booking before it
 * existed — so a Trips screen calling these must pass `trip.requestId`.
 */

export function fetchPaymentVerification(tripRequestId: number): Promise<PaymentVerificationDetails> {
  return api.get<PaymentVerificationDetails>(`/api/admin/payments/${tripRequestId}/verification`)
}

/**
 * Confirms the money arrived.
 *
 * Refused by the server for card payments, whose status belongs to the gateway.
 * The dashboard hides the action in that case too, but the server is the one
 * that decides.
 */
export function verifyPayment(tripRequestId: number, comment?: string): Promise<PaymentVerificationDetails> {
  return api.post<PaymentVerificationDetails>(`/api/admin/payments/${tripRequestId}/verify`, { comment })
}

/** Records that the money is not there. The comment is required by the server. */
export function rejectPayment(tripRequestId: number, comment: string): Promise<PaymentVerificationDetails> {
  return api.post<PaymentVerificationDetails>(`/api/admin/payments/${tripRequestId}/reject`, { comment })
}

/**
 * Switches a booking from credit card to Instapay.
 *
 * The one conversion the API supports, and the only one it ever needs to: a
 * customer whose card checkout failed rings up and says they will transfer the
 * money instead. Before this the booking was stuck — the card payment was never
 * going to arrive, a driver cannot be assigned to a card booking still awaiting
 * payment, and the only way out was to cancel and re-book, losing the reference,
 * the promo and any points already spent.
 *
 * Moves no money and marks nothing paid. The booking stays pending and joins the
 * verification queue, where the transfer is confirmed the same way every other
 * Instapay payment is.
 *
 * Offer this only where `payment.canConvertToInstapay` is true — the server
 * applies the same rule and answers 409 `METHOD_CHANGE_NOT_ALLOWED` otherwise.
 */
export function changePaymentMethod(
  tripRequestId: number,
  change: PaymentMethodChangeRequest,
): Promise<PaymentVerificationDetails> {
  return api.patch<PaymentVerificationDetails>(`/api/admin/payments/${tripRequestId}/method`, change)
}

/**
 * Attaches a proof of payment on the customer's behalf.
 *
 * The counterpart to the customer's own upload in the mobile app, and it exists
 * because the receipt often does not arrive through the app at all: a customer
 * who was switched to Instapay over the phone sends their screenshot to whoever
 * they were speaking to.
 *
 * Sent as multipart under the field name `file`, which is what the endpoint
 * reads. Uploading settles nothing — the booking stays unpaid until a
 * verification decision is recorded — but it is what satisfies the proof
 * requirement standing between the booking and a driver.
 */
export function uploadPaymentProof(
  tripRequestId: number,
  file: File,
): Promise<PaymentProofUploadResponse> {
  const form = new FormData()
  form.append('file', file)
  return api.upload<PaymentProofUploadResponse>(`/api/admin/payments/${tripRequestId}/proof`, form)
}

/**
 * Loads the proof image as a blob URL.
 *
 * The caller **must** revoke the returned `objectUrl` when the image is no
 * longer displayed — see {@link apiBlobRequest}.
 */
export function fetchPaymentProof(tripRequestId: number): Promise<{ objectUrl: string; contentType: string }> {
  return apiBlobRequest(`/api/admin/payments/${tripRequestId}/proof`)
}

/** Every points movement this booking caused, newest first. Empty for most bookings. */
export function fetchBookingPointsHistory(tripRequestId: number): Promise<PointsTransaction[]> {
  return api.get<PointsTransaction[]>(`/api/admin/payments/${tripRequestId}/points`)
}

export function fetchInstapayConfig(): Promise<InstapayConfig | null> {
  return api.get<InstapayConfig | null>('/api/admin/payments/instapay-config')
}

/**
 * Replaces the Instapay account customers are told to transfer to.
 *
 * `PUT` because there is one account row and this replaces it. The server
 * refuses to activate an account with a blank required field — an active row
 * with no account number would send customers nowhere.
 */
export function updateInstapayConfig(update: InstapayConfigUpdate): Promise<InstapayConfig> {
  return api.put<InstapayConfig>('/api/admin/payments/instapay-config', update)
}
